import SHA256 from 'crypto-js/sha256'
import Hex from 'crypto-js/enc-hex'
import { type OsContextValue } from '@wppopen/react'
import { type GtmEventSchema } from './types'
import { type SelectedHierarchy, type SelectedHierarchyItem } from '@wppopen/core/types/hierarchy'
import { DefaultHierarchyLevelType, type HierarchyLevelType } from '@wppopen/core/types'

/**
 * @param hierarchyMapping - The hierarchy mapping object from the osContext.
 * @param type the type of the hierarchy item to search for (either predefined in the enum provided, or custom).
 * @description This function retrieves the name of a hierarchy item based on its type.
 * It searches through the hierarchy mapping to find the item that matches the specified type.
 * If no matching item is found, it returns 'undefined'.
 * @returns The name of the hierarchy item if found, otherwise 'undefined'.
 */
export const getHierarchyItemName = (
  hierarchyMapping: SelectedHierarchy['mapping'] | undefined,
  type: HierarchyLevelType,
) => {
  if (!hierarchyMapping) return undefined

  const hierarchyItem = Object.values(hierarchyMapping).find((item: SelectedHierarchyItem) => {
    return item.type === type
  })
  return hierarchyItem?.name || undefined
}

/**
 *
 * @param osContextValue - The osContext object from the WPP Open React library.
 * @description This function maps the user details from the osContext to a data layer format.
 * @returns
 */
export const getGtmUserProfileDataFromContext = (osContextValue: OsContextValue | null) => {
  const userDetails = osContextValue?.osContext?.userDetails

  // The locationId field is available at runtime but not in the type definition, hence ts ignored here
  // @ts-ignore
  const locationId = osContextValue?.osContext?.userDetails?.locationId as string

  const hashedEmail = userDetails?.email ? SHA256(userDetails.email.toLowerCase().trim()).toString(Hex) : 'unknown'

  return {
    user_id: hashedEmail,
    user_company: userDetails?.company || 'unknown',
    user_country_code: userDetails?.countryAlpha2Code || 'unknown',
    user_department: userDetails?.department || 'unknown',
    user_job_role: userDetails?.jobTitle || 'unknown',
    user_region: locationId?.split('-')[0] || 'unknown',
  } satisfies GtmEventSchema
}

export const getContextHierarchyItems = (osContextValue: OsContextValue | null) => {
  const hierarchy = osContextValue?.osContext?.hierarchy
  return {
    workspace_market: getHierarchyItemName(hierarchy?.mapping, DefaultHierarchyLevelType.Market),
    workspace_industry: getHierarchyItemName(hierarchy?.mapping, DefaultHierarchyLevelType.Industry),
    workspace_brand: getHierarchyItemName(hierarchy?.mapping, DefaultHierarchyLevelType.Brand),
    workspace_client: getHierarchyItemName(hierarchy?.mapping, DefaultHierarchyLevelType.Client),
    workspace_region: getHierarchyItemName(hierarchy?.mapping, DefaultHierarchyLevelType.Region),
    workspace_custom: getHierarchyItemName(hierarchy?.mapping, 'CUSTOM'),
  }
}
